import { useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Mail } from "lucide-react";
import { EmailPreviewDialog } from "@/components/t4r/learners/EmailPreviewDialog";
import { useLearnerEmails } from "@/components/LearnerEmailsSwitch";

const empty = { email: "", name: "", course_ids: [] };

export const InviteLearnerDialog = ({ open, onClose, courses, defaultCourseId, onInvited }) => {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [emailsOn] = useLearnerEmails("retrofit");
  useEffect(() => { if (open) setForm({ ...empty, course_ids: defaultCourseId ? [defaultCourseId] : [] }); }, [open, defaultCourseId]);
  const toggle = (id) => setForm((f) => ({ ...f, course_ids: f.course_ids.includes(id) ? f.course_ids.filter((x) => x !== id) : [...f.course_ids, id] }));

  const send = async () => {
    if (!form.email.trim() || !form.name.trim()) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/lms/admin/invite", form);
      const { data: sw } = await api.get("/settings/learner-emails");
      if (sw.retrofit_enabled) toast.success(`Invite sent to ${form.email}`);
      else {
        try { await navigator.clipboard.writeText(data.invite_url); } catch { window.prompt("Copy the invite link:", data.invite_url); }
        toast.success("Learner added — invite link copied", { description: "Learner emails are off, so nothing was emailed. Paste the link into WhatsApp, a text or your own email.", duration: 10000 });
      }
      onInvited(); onClose();
    }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="invite-dialog">
        <DialogHeader><DialogTitle>Invite a learner</DialogTitle><DialogDescription className="text-xs" data-testid="invite-dialog-description">{emailsOn === false ? "Learner emails are off — you'll get an invite link to send them yourself." : "They'll get an email to set their password."}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" /></div>
          <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" /></div>
          <div>
            <Label className="text-xs">Enrol on courses</Label>
            <div className="mt-1 max-h-[180px] divide-y divide-slate-50 overflow-y-auto rounded-md border border-border bg-white">
              {courses.map((c) => (
                <label key={c.id} className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs hover:bg-green-50">
                  <input type="checkbox" checked={form.course_ids.includes(c.id)} onChange={() => toggle(c.id)} data-testid={`invite-course-${c.id}`} />
                  <span className="flex-1 truncate">{c.title}</span>{!c.is_published && <span className="text-[10px] text-slate-400">draft</span>}
                </label>
              ))}
              {courses.length === 0 && <p className="p-3 text-xs italic text-muted-foreground">Create a course first.</p>}
            </div>
          </div>
        </div>
        <DialogFooter className="sm:justify-between"><Button variant="ghost" className="gap-1 text-green-800" onClick={() => setPreviewing(true)} data-testid="invite-preview-email"><Mail size={14} /> Preview email</Button><div className="flex gap-2"><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-green-700 text-white hover:bg-green-800" disabled={busy} onClick={send} data-testid="invite-send">Send invite</Button></div></DialogFooter>
        <EmailPreviewDialog open={previewing} onClose={() => setPreviewing(false)} name={form.name} courseIds={form.course_ids} />
      </DialogContent>
    </Dialog>
  );
};
